import React from 'react'
import styled, { css } from 'styled-components'
import { useLocation } from 'react-router-dom'
import { GAHelper } from 'core/ga'
import { GA_EVENT } from 'core/ga/enums'
import { getPartnerId, getThemeType } from 'core/singleton'
import { useRecoilValue } from 'recoil'
import { STORE_MARKETING_GTM } from 'core/store/analysis'
import { getLocation, layoutSmall } from 'core/util/default'
import { THEME_TYPE } from 'core/constants'

interface TopBannerSliderItemProps {
  item: GetTopBannerItem
  isBridge: boolean
  index: number
  totalCount: number
  onClickBanner: (item: GetTopBannerItem) => boolean
  clickable: boolean
}

enum TopBannerLinkTypes {
  NONE = 'none',
  MOVE = 'move',
  OPEN = 'open',
}

const TopBannerSliderItem: React.FC<TopBannerSliderItemProps> = (props) => {
  const { item, isBridge, index, onClickBanner, clickable, totalCount } = props
  const { img, link: bannerLink, linkType, badge, topBannerName } = item
  const partnerId = getPartnerId()
  const location = useLocation()
  const marketingGTM = useRecoilValue(STORE_MARKETING_GTM)

  const onClickSlide = (e: React.MouseEvent) => {
    e.preventDefault()

    new GAHelper().sendEvent(GA_EVENT.CLICK_BANNER, {
      partnersId: partnerId,
      number_of_item: `${index + 1}/${totalCount}`,
      location: getLocation(location),
      banner_type: linkType,
    })

    if (!clickable || linkType === TopBannerLinkTypes.NONE) return
    if (onClickBanner(item)) return

    let link = bannerLink
    if (marketingGTM?.checkAvailability()) {
      const queryStringPrefix = link.indexOf('?') >= 0 ? '&' : '?'
      link = link.concat(`${queryStringPrefix}_gl=${marketingGTM.getGl()}`)
    }

    // 팝업차단 문제 때문에 click 이벤트 핸들러 스코프 안에서 이동처리
    if (linkType === TopBannerLinkTypes.MOVE) {
      window.location.href = link
      return
    }
    if (linkType === TopBannerLinkTypes.OPEN) {
      window.open(link, '_blank')
      return
    }
    return
  }

  const isLinkTag = linkType !== TopBannerLinkTypes.NONE
  const CustomBannerTag = isBridge ? 'button' : linkType === TopBannerLinkTypes.NONE ? 'div' : 'a'
  const slideImgAlt = !badge.title ? topBannerName : ''

  return (
    <StyledSliderItem className="slick-slider" badge={badge}>
      <CustomBannerTag
        {...(isLinkTag ? { href: '#' } : { tabIndex: 0 })}
        onClick={onClickSlide}
        className="slide-wrap"
        draggable="false"
        tabIndex={0}
        title={slideImgAlt}
      >
        <div className="slide-content">
          <div className="slide-text">
            <span className="slide-badge">{badge.name}</span>
            <strong className="slide-title">{badge.title}</strong>
            <span className="slide-subTitle">{badge.subTitle}</span>
          </div>
          <div className="slide-image">
            <img src={img} alt={slideImgAlt} />
          </div>
        </div>
      </CustomBannerTag>
    </StyledSliderItem>
  )
}

export default TopBannerSliderItem

const StyledSliderItem = styled.div<{ badge: GetBadgeResponse }>`
  background: ${({ badge }) => {
    const themeType = getThemeType()
    if (badge.bg && themeType === THEME_TYPE.DEFAULT) {
      return badge.bg
    }
    if (badge.themeBBg && themeType === THEME_TYPE.TYPE_B) {
      return badge.themeBBg
    }
    return '#fff'
  }};
  .slide-wrap {
    position: relative;
    display: block;
    width: 100%;
    padding: 0;
    background-color: transparent;
    border: none;
    outline-offset: 0;
    text-align: left;
    &:before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 1;
    }
  }
  .slide-content {
    position: relative;
    max-width: 750px;
    height: 320px;
    margin: 0 auto;
  }
  .slide-image {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    img {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  .slide-text {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    padding-top: 40px;
    padding-left: 40px;
    z-index: 1;
  }
  .slide-badge {
    display: inline-block;
    padding: 0 9px;
    border-radius: 20px;
    background: ${({ badge }) => {
      if (badge.badgeBg) {
        return badge.badgeBg
      }
      return 'rgba(0,0,0,0.2)'
    }};
    color: ${({ badge }) => {
      if (badge.color) {
        return badge.color
      }
      return '#fff'
    }};
    font-size: 21px;
    font-weight: bold;
    line-height: 1.6;
  }
  .slide-title {
    display: block;
    margin-top: 20px;
    font-size: 37px;
    font-weight: bold;
    line-height: 1.28;
    white-space: pre-wrap;
    -webkit-line-clamp: 2;
    color: ${({ badge }) => {
      if (badge.titleColor) {
        return badge.titleColor
      }
      return '#000'
    }};
  }
  .slide-subTitle {
    display: block;
    margin-top: 13px;
    font-size: 25px;
    color: ${({ badge }) => {
      if (badge.subTitleColor) {
        return badge.subTitleColor
      }
      return '#000'
    }};
  }
  ${layoutSmall(css`
    /* mobile Layout */
    .slide-content {
      height: auto;
      padding-top: 42.7%;
    }
    .slide-text {
      padding-left: 25px;
      padding-top: 5.6%;
    }
    .slide-badge {
      padding: 0 5px;
      font-size: 10px;
    }
    .slide-title {
      margin-top: 10px;
      font-size: 18px;
    }
    .slide-subTitle {
      margin-top: 5px;
      font-size: 12px;
    }
  `)};
`
